import { AutorenewOutlined } from '@mui/icons-material';
import { Button, FormHelperText, Grid, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { AryAlertTypes } from 'framework/base/components/AryAlert';
import { formatMoneyAmount } from 'framework/base/components/AryNumberFormatter';
import AryWrapper from 'framework/base/components/AryWrapper';
import { GET_BANK_BOOK_STATUS } from 'framework/base/constants/aryGraphqlSchemas';
import useAryAlert from 'framework/base/hooks/useAryAlert';
import { useAryLazyQuery } from 'framework/base/hooks/useAryGraphql';
import useLayoutTypeContext from 'framework/base/hooks/useLayoutTypeContext';
import useShowCurrency from 'framework/base/hooks/useShowCurrency';
import AryLoadingAnimation from 'framework/desktop/AryLoadingAnimation';
import React, {
  Suspense,
  forwardRef,
  lazy,
  useEffect,
  useImperativeHandle,
  useState
} from 'react';
import useFrameworkTranslation from 'framework/language/useFrameworkTranslation';

const UnknownBankTransactionsModalDesktop = lazy(() =>
  import('pages/desktop/common-modals/UnknownBankTransactionsModal')
);

const UnknownBankTransactionsModalMobile = lazy(() =>
  import('pages/mobile/common-modals/UnknownBankTransactionsModal')
);

const BankStatus = forwardRef(
  (
    {
      isDeposit,
      bankId,
      handleOldTransactionSelected,
      isNewTransaction,
      handleIsNewTransactionChange,
      documentIdError,
      documentIdHelperText,
      height,
      hideTransactionType,
      isCalculationDoneHandler,
      message,
      showStatus,
      amount
    },
    ref
  ) => {
    const theme = useTheme();
    const { t } = useFrameworkTranslation();
    const [AryAlert, showAlert] = useAryAlert();
    const showCurrency = useShowCurrency();
    const layoutType = useLayoutTypeContext();

    const [
      getBankBookStatus,
      { data: getBankBookStatusQueryData, refetch: refetchGetBankBookStatus }
    ] = useAryLazyQuery({
      gqlConfig: GET_BANK_BOOK_STATUS,
      items: `
        currency
        balance
        isDebtor
      `
    });

    const [
      unknownBankTransactionsModalOpen,
      setUnknownBankTransactionsModalOpen
    ] = useState(false);

    useImperativeHandle(ref, () => ({
      getBankBookStatus: () => refetchGetBankBookStatus()
    }));

    useEffect(() => {
      if (bankId) {
        getBankBookStatus({ bankId });
        if (isCalculationDoneHandler) {
          isCalculationDoneHandler(true);
        }
      }
    }, [bankId]);

    return (
      <>
        <AryWrapper
          backgroundColor={theme.wrapper.grey50.backgroundColor}
          style={{
            padding: '0.375rem .5rem !important',
            height: height || '2rem',
            alignContent: 'center',
            display: 'flex !important'
          }}
        >
          {bankId ? (
            <Grid container alignContent="center" alignItems="center">
              {!hideTransactionType && (
                <Grid item xs={4} alignContent="center" alignItems="center">
                  <Button
                    value={isNewTransaction}
                    onClick={handleIsNewTransactionChange}
                    variant="contained"
                    tabIndex={-1}
                    size="small"
                    endIcon={<AutorenewOutlined />}
                    sx={{
                      width: '4.6rem !important',
                      padding: '0.125rem 0.25rem !important',
                      minWidth: '3.563rem !important',
                      height: '100%',
                      boxShadow: 'inherit',
                      fontSize: '0.75rem',
                      fontWeight: '700',
                      color: theme.text.primary500.color,
                      background: theme.chip.blue.background,
                      '&:hover': {
                        background: '#CFEEFB',
                        boxShadow:
                          '0px 2px 2px 0px #051C310A, 0px 2px 3px 0px #051C3114'
                      }
                    }}
                  >
                    {isNewTransaction ? t('Known') : t('Unknown')}
                  </Button>
                </Grid>
              )}
              <Grid
                item
                xs={isNewTransaction ? 8 : 5}
                gap={1}
                display="flex"
                alignItems="center"
              >
                <Typography
                  variant="h7Bold"
                  color={theme.text.secondary.color}
                  ml={1}
                >
                  {t('Inventory')}:
                </Typography>
                <Typography
                  variant="h7Bold"
                  sx={{
                    color: getBankBookStatusQueryData
                      ? getBankBookStatusQueryData.isDebtor
                        ? theme.text.red.color
                        : theme.text.green.color
                      : theme.text.primary.color
                  }}
                >
                  {getBankBookStatusQueryData
                    ? `${formatMoneyAmount(
                        getBankBookStatusQueryData.balance
                      )} ${showCurrency({
                        title: getBankBookStatusQueryData.currency
                      })}`
                    : '-'}
                </Typography>
              </Grid>
              {showStatus && (
                <Grid
                  item
                  xs={isNewTransaction ? 8 : 5}
                  gap={1}
                  display="flex"
                  alignItems="center"
                >
                  <Typography
                    variant="h7Bold"
                    color={theme.text.secondary.color}
                    ml={1}
                  >
                    {t('Status')}:
                  </Typography>
                  <Typography
                    variant="h7Bold"
                    sx={{
                      color: getBankBookStatusQueryData
                        ? getBankBookStatusQueryData.isDebtor
                          ? theme.text.red.color
                          : theme.text.green.color
                        : theme.text.primary.color
                    }}
                  >
                    {getBankBookStatusQueryData
                      ? getBankBookStatusQueryData.isDebtor
                        ? t('Debtor')
                        : t('Creditor')
                      : '-'}
                  </Typography>
                </Grid>
              )}
              {isNewTransaction
                ? null
                : !hideTransactionType && (
                    <Grid item xs={3} alignContent="center">
                      <Button
                        fullWidth
                        size="small"
                        tabIndex={-1}
                        sx={{
                          width: '4.6rem !important',
                          padding: '0.125rem 0.25rem !important',
                          minWidth: '3.563rem !important',
                          height: '100%',
                          boxShadow: 'inherit',
                          fontSize: '0.75rem',
                          fontWeight: '700'
                        }}
                        onClick={() => {
                          if (bankId) {
                            setUnknownBankTransactionsModalOpen(true);
                          } else {
                            showAlert(
                              AryAlertTypes.WARNING,
                              t('bankTransaction.selectBankFirst')
                            );
                          }
                        }}
                      >
                        {t('SelectDocument')}
                      </Button>
                    </Grid>
                  )}
            </Grid>
          ) : (
            <Grid item>
              <Typography variant="c3Bold" color={theme.text.secondary.color}>
                {message}
              </Typography>
            </Grid>
          )}
          {unknownBankTransactionsModalOpen && layoutType !== 'mobile' && (
            <Suspense fallback={<AryLoadingAnimation />}>
              <UnknownBankTransactionsModalDesktop
                isDeposit={isDeposit}
                open={unknownBankTransactionsModalOpen}
                handleClose={() => setUnknownBankTransactionsModalOpen(false)}
                bankId={bankId}
                handleOldTransactionSelected={handleOldTransactionSelected}
                amount={amount}
              />
            </Suspense>
          )}
          {unknownBankTransactionsModalOpen && layoutType === 'mobile' && (
            <Suspense fallback={<AryLoadingAnimation />}>
              <UnknownBankTransactionsModalMobile
                isDeposit={isDeposit}
                open={unknownBankTransactionsModalOpen}
                handleClose={() => setUnknownBankTransactionsModalOpen(false)}
                bankId={bankId}
                handleOldTransactionSelected={handleOldTransactionSelected}
                amount={amount}
              />
            </Suspense>
          )}
          {AryAlert}
        </AryWrapper>
        {documentIdError && documentIdHelperText && !isNewTransaction && (
          <FormHelperText error={documentIdError}>
            {documentIdHelperText}
          </FormHelperText>
        )}
      </>
    );
  }
);

export default BankStatus;
